import { StyleSheet } from '@react-pdf/renderer';
import { PDF_FONTS } from './fonts';

// Import font registration to ensure fonts are loaded
import './fonts';

/**
 * Common PDF Styles
 * 
 * Centralized styling for PDF components using @react-pdf/renderer StyleSheet.
 * Provides consistent styling across all PDF documents.
 * Uses Montserrat font family to match the main application.
 */

// Color palette for PDFs
export const PDF_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#424242',
  TEXT: '#333333',
  TEXT_LIGHT: '#666666',
  TEXT_MUTED: '#999999',
  BORDER: '#e0e0e0',
  BACKGROUND: '#ffffff',
  ACCENT: '#f5f5f5',
  ERROR: '#d32f2f',
  SUCCESS: '#388e3c',
  WARNING: '#f57c00',
} as const;

// Font sizes
export const PDF_FONT_SIZES = {
  TITLE: 18,
  SUBTITLE: 16,
  HEADING: 14,
  SUBHEADING: 12,
  BODY: 10,
  CAPTION: 9,
  SMALL: 8,
} as const;

// Spacing values
export const PDF_SPACING = {
  XS: 2,
  SM: 4,
  MD: 8,
  LG: 12,
  XL: 16,
  XXL: 24,
} as const;

// Common PDF styles
export const commonPDFStyles = StyleSheet.create({
  // Page styles
  page: {
    flexDirection: 'column',
    backgroundColor: PDF_COLORS.BACKGROUND,
    padding: 30,
    fontFamily: PDF_FONTS.PRIMARY,
    fontSize: PDF_FONT_SIZES.BODY,
    color: PDF_COLORS.TEXT,
  },
  
  // Layout styles
  container: {
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'column',
  },
  
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  
  column: {
    flexDirection: 'column',
  },
  
  flex1: {
    flex: 1,
  },
  
  // Header styles
  header: {
    flexDirection: 'row',
    marginBottom: PDF_SPACING.LG,
    paddingBottom: PDF_SPACING.XS,
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.BORDER,
  },
  
  headerLeft: {
    flex: 1,
    alignItems: 'flex-start',
  },
  
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  
  // Typography styles
  title: {
    fontSize: PDF_FONT_SIZES.TITLE,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: PDF_SPACING.MD,
    color: PDF_COLORS.TEXT,
  },
  
  subtitle: {
    fontSize: PDF_FONT_SIZES.SUBTITLE,
    fontWeight: 'bold',
    marginBottom: PDF_SPACING.SM,
    color: PDF_COLORS.TEXT,
  },
  
  heading: {
    fontSize: PDF_FONT_SIZES.HEADING,
    fontWeight: 'bold',
    marginBottom: PDF_SPACING.SM,
    color: PDF_COLORS.TEXT,
  },
  
  subheading: {
    fontSize: PDF_FONT_SIZES.SUBHEADING,
    fontWeight: 'bold',
    marginBottom: PDF_SPACING.XS,
    color: PDF_COLORS.TEXT,
  },
  
  body: {
    fontSize: PDF_FONT_SIZES.BODY,
    lineHeight: 1.4,
    marginBottom: PDF_SPACING.XS,
    color: PDF_COLORS.TEXT,
  },
  
  caption: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    color: PDF_COLORS.TEXT_LIGHT,
    marginBottom: PDF_SPACING.XS,
  },
  
  small: {
    fontSize: PDF_FONT_SIZES.SMALL,
    color: PDF_COLORS.TEXT_MUTED,
  },
  
  // Text formatting
  bold: {
    fontWeight: 'bold',
  },
  
  italic: {
    fontStyle: 'italic',
  },
  
  underline: {
    textDecoration: 'underline',
  },
  
  // Alignment
  textLeft: {
    textAlign: 'left',
  },
  
  textCenter: {
    textAlign: 'center',
  },
  
  textRight: {
    textAlign: 'right',
  },
  
  // Spacing utilities
  marginTop: {
    marginTop: PDF_SPACING.MD,
  },
  
  marginBottom: {
    marginBottom: PDF_SPACING.XS,
  },
  
  marginVertical: {
    marginTop: PDF_SPACING.MD,
    marginBottom: PDF_SPACING.MD,
  },
  
  paddingSmall: {
    padding: PDF_SPACING.SM,
  },
  
  paddingMedium: {
    padding: PDF_SPACING.MD,
  },
  
  // Borders and dividers
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.BORDER,
    marginVertical: PDF_SPACING.MD,
  },
  
  border: {
    borderWidth: 1,
    borderColor: PDF_COLORS.BORDER,
  },
  
  borderTop: {
    borderTopWidth: 1,
    borderTopColor: PDF_COLORS.BORDER,
  },
  
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.BORDER,
  },
  
  // Background colors
  backgroundLight: {
    backgroundColor: PDF_COLORS.ACCENT,
  },
  
  // Table styles
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: PDF_COLORS.BORDER,
  },
  
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.BORDER,
    minHeight: 25,
    alignItems: 'center',
  },
  
  tableHeader: {
    backgroundColor: PDF_COLORS.ACCENT,
    fontWeight: 'bold',
  },
  
  tableCell: {
    flex: 1,
    padding: PDF_SPACING.SM,
    fontSize: PDF_FONT_SIZES.BODY,
  },
  
  tableCellBorder: {
    borderRightWidth: 1,
    borderRightColor: PDF_COLORS.BORDER,
  },
  
  // Section styles
  section: {
    marginBottom: PDF_SPACING.XS,
  },
  
  sectionHeader: {
    fontSize: PDF_FONT_SIZES.HEADING,
    fontWeight: 'bold',
    marginBottom: PDF_SPACING.MD,
    paddingBottom: PDF_SPACING.SM,
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLORS.BORDER,
  },
  
  sectionContent: {
    paddingLeft: 0,
    marginBottom: PDF_SPACING.XS,
  },
  
  // Footer styles
  footer: {
    marginTop: 'auto',
    paddingTop: PDF_SPACING.LG,
    borderTopWidth: 1,
    borderTopColor: PDF_COLORS.BORDER,
  },
  
  // Signature styles
  signature: {
    alignItems: 'center',
    marginTop: PDF_SPACING.LG,
  },
  
  signatureImage: {
    width: 100,
    height: 50,
    marginBottom: PDF_SPACING.SM,
  },
  
  signatureName: {
    fontSize: PDF_FONT_SIZES.BODY,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  signatureTitle: {
    fontSize: PDF_FONT_SIZES.CAPTION,
    color: PDF_COLORS.TEXT_LIGHT,
    textAlign: 'center',
  },
  
  // Logo styles
  logo: {
    width: 200, // Increased from 150
    height: 100, // Increased from 75
    objectFit: 'contain',
  },
  
  // Status indicators
  statusSuccess: {
    color: PDF_COLORS.SUCCESS,
  },
  
  statusError: {
    color: PDF_COLORS.ERROR,
  },
  
  statusWarning: {
    color: PDF_COLORS.WARNING,
  },
  
  // Page break utilities
  pageBreakBefore: {
    breakBefore: 'page',
  },
  
  pageBreakAfter: {
    breakAfter: 'page',
  },
  
  pageBreakAvoid: {
    breakInside: 'avoid',
  },
});

